import { AnketaRolesWithPermissions } from "../types";

/**
 * Canonical role => resource => action seed (also the fail-safe fallback when the
 * runtime `role_permissions` table is empty/unreadable). Shaped per
 * .claude/AUTH_RECOMMENDATION.md §5.
 *
 * Scope (own vs all) is NOT encoded here - the matrix is a flat boolean grant.
 * The route enforces scope: `own` via the questionnaire membership check, `all`
 * via the attribute narrows passed to `hasPermission`:
 *   - create / view-all              -> { isInternal: true }
 *   - delete / archive (destructive) -> { isAutonubil: true }
 * A `true` cell here is a *necessary* grant; the narrow makes it sufficient only
 * for the right organization. The gated opsanio features (updates/contact/
 * appliance/documentation/hardware) additionally sit behind the Opsanio baseline gate
 * (getUsersOpsanioAttributes) - matrix grant alone is not enough for those.
 */
export const ROLES = {
  admin: {
    questionnaires: {
      create: true,
      delete: true,
      update: true,
      view: true,
      invite: true,
      archive: true,
    },
    organizations: {
      create: true,
      update: true,
      view: true,
    },
    "opsanio/updates": { view: true, create: true, update: true, delete: true },
    "opsanio/contact": { view: true, create: true, update: true, delete: true },
    "opsanio/appliance": { view: true, create: true, update: true, delete: true },
    "opsanio/documentation": { view: true, create: true, update: true, delete: true },
    "opsanio/hardware": { view: true, create: true },
    "opsanio/fleet": { view: true },
    "opsanio/hardening": { view: true, update: true },
    "opsanio/bootstrap": { create: true },
    "opsanio/prtg": { create: true },
    "opsanio/otobo": { create: true },
    "opsanio/pangolin": { view: true, create: true },
    "opsanio/opl": { view: true, create: true, update: true, delete: true },
  },
  moderator: {
    questionnaires: {
      // create is granted but route-narrowed to internal orgs ({ isInternal }).
      create: true,
      delete: false,
      update: true,
      view: true,
      invite: true,
      archive: false,
    },
    organizations: {
      create: false,
      update: true,
      view: true,
    },
    "opsanio/updates": { view: false, create: false, update: false, delete: false },
    "opsanio/contact": { view: false, create: false, update: false, delete: false },
    "opsanio/appliance": { view: false, create: false, update: false, delete: false },
    "opsanio/documentation": { view: false, create: false, update: false, delete: false },
    "opsanio/hardware": { view: false, create: false },
    "opsanio/fleet": { view: false },
    "opsanio/hardening": { view: false, update: false },
    "opsanio/bootstrap": { create: false },
    "opsanio/prtg": { create: false },
    // otobo provisioning is open to internal moderators (route-narrowed isInternal).
    "opsanio/otobo": { create: true },
    "opsanio/pangolin": { view: false, create: false },
    "opsanio/opl": { view: false, create: false, update: false, delete: false },
  },
  user: {
    questionnaires: {
      // create / invite are granted but route-narrowed to internal orgs / membership.
      create: true,
      delete: false,
      update: true,
      view: true,
      invite: true,
      archive: false,
    },
    organizations: {
      create: false,
      update: false,
      view: true,
    },
    "opsanio/updates": { view: false, create: false, update: false, delete: false },
    "opsanio/contact": { view: false, create: false, update: false, delete: false },
    "opsanio/appliance": { view: false, create: false, update: false, delete: false },
    "opsanio/documentation": { view: false, create: false, update: false, delete: false },
    "opsanio/hardware": { view: false, create: false },
    "opsanio/fleet": { view: false },
    "opsanio/hardening": { view: false, update: false },
    "opsanio/bootstrap": { create: false },
    "opsanio/prtg": { create: false },
    "opsanio/otobo": { create: true },
    "opsanio/pangolin": { view: false, create: false },
    "opsanio/opl": { view: false, create: false, update: false, delete: false },
  },
  // Infra / opsanio operator. Questionnaire capability ~ moderator (no invite,
  // no destructive ops); full opsanio operator capability (gate-limited for the
  // gated features) plus the standalone infra ops (route-narrowed isInternal).
  technical: {
    questionnaires: {
      create: true,
      delete: false,
      update: true,
      view: true,
      invite: false,
      archive: false,
    },
    organizations: {
      create: false,
      update: false,
      view: true,
    },
    "opsanio/updates": { view: true, create: true, update: true, delete: true },
    "opsanio/contact": { view: true, create: true, update: true, delete: true },
    "opsanio/appliance": { view: true, create: true, update: true, delete: true },
    "opsanio/documentation": { view: true, create: true, update: true, delete: true },
    "opsanio/hardware": { view: true, create: true },
    "opsanio/fleet": { view: true },
    "opsanio/hardening": { view: true, update: true },
    "opsanio/bootstrap": { create: true },
    "opsanio/prtg": { create: true },
    "opsanio/otobo": { create: true },
    "opsanio/pangolin": { view: true, create: true },
    "opsanio/opl": { view: true, create: true, update: true, delete: true },
  },
  /**
   * Sales colleague. Identified by the `opsanio-sales` realm role and NOT by the
   * Opsanio gate, which they do not pass - they are not operators.
   *
   * Every cell here is **appliance-scoped by questionnaire membership**: the
   * matrix says what a sales user may do, `functions/auth/applianceMembership.ts`
   * says on which appliance. A grant here alone authorises nothing; without a
   * membership the answer is still no.
   */
  sales: {
    questionnaires: {
      create: false,
      delete: false,
      update: false,
      view: true,
      invite: false,
      archive: false,
    },
    organizations: { create: false, update: false, view: true },
    "opsanio/updates": { view: false, create: false, update: false, delete: false },
    "opsanio/contact": { view: false, create: false, update: false, delete: false },
    // Look at the appliance; never change the running customer system.
    "opsanio/appliance": { view: true, create: false, update: false, delete: false },
    "opsanio/documentation": { view: false, create: false, update: false, delete: false },
    // The fleet inventory is the whole spare pool across all customers - there is
    // no appliance to scope it to, so sales does not get it.
    "opsanio/hardware": { view: false, create: false },
    "opsanio/fleet": { view: false },
    "opsanio/hardening": { view: false, update: false },
    "opsanio/bootstrap": { create: false },
    "opsanio/prtg": { create: false },
    "opsanio/otobo": { create: false },
    "opsanio/pangolin": { view: false, create: false },
    // Full participation in the round, minus deletion.
    "opsanio/opl": { view: true, create: true, update: true, delete: false },
  },
} as const satisfies AnketaRolesWithPermissions;
